import api from './api';
import safeStorage from './safeStorage';
import { buildE2eePayloadV1 } from './messageE2ee';
import {
  decodeAccessTokenUserId,
  ensureMessagingIdentity,
  getLocalMessagingSecretKey,
  getOrCreateMessagingDeviceId,
} from './messagingIdentity';

/** Same POST as messagingService.registerMessagingPublicKey — avoids importing messagingService (circular). */
const minimalKeyRegistrationService = {
  registerMessagingPublicKey: (publicKeyB64) =>
    api.post('/messaging/user-keys/', {
      public_key: publicKeyB64,
      device_id: getOrCreateMessagingDeviceId(),
    }),
};

function getCurrentUserIdFromToken() {
  const token = safeStorage.getItem('token');
  return token ? decodeAccessTokenUserId(token) : null;
}

/** Match backend club announcement participant sync: members + creator. */
export function clubParticipantIdsForBroadcast(club) {
  const memberIds = (club?.members || []).map((m) => m.id);
  const cb = club?.created_by;
  const creatorId = typeof cb === 'object' ? cb?.id : cb;
  return [...new Set([...memberIds, creatorId].filter((x) => x != null))];
}

function participantIdsFromConversation(conversation) {
  const parts = conversation?.participants;
  if (!Array.isArray(parts)) return [];
  return parts.map((p) => p.id).filter((id) => id != null);
}

/**
 * Build wrap targets and pubkey map for every registered device of each participant.
 */
async function fetchWrapTargetsForSend(messagingService, userIds, meId, localPublicKeyB64, localDeviceId) {
  const ids = [...new Set(userIds.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0))].sort(
    (a, b) => a - b
  );
  if (ids.length === 0) {
    throw new Error('No participants');
  }
  const keysRaw = await messagingService.lookupMessagingPublicKeys(ids);
  const pubMap = {};
  const wrapKeys = [];
  for (const uid of ids) {
    const devices = keysRaw[String(uid)];
    const list = Array.isArray(devices) ? devices : [];
    if (list.length === 0) {
      throw new Error(
        `User ${uid} has not registered messaging encryption keys yet. They need to open Messages once.`
      );
    }
    for (const d of list) {
      const composite = `${uid}:${d.device_id}`;
      wrapKeys.push(composite);
      if (meId != null && Number(uid) === Number(meId) && String(d.device_id) === String(localDeviceId)) {
        pubMap[composite] = localPublicKeyB64;
      } else {
        pubMap[composite] = d.public_key;
      }
    }
  }
  return { wrapKeys, pubMap };
}

/**
 * Preflight before creating a conversation: every future participant must already have a public key on the server.
 */
export async function assertConversationPeersHaveMessagingKeys(messagingService, userIds) {
  const { publicKeyB64 } = await ensureMessagingIdentity(minimalKeyRegistrationService);
  const meId = getCurrentUserIdFromToken();
  const localDeviceId = getOrCreateMessagingDeviceId();
  await fetchWrapTargetsForSend(messagingService, userIds, meId, publicKeyB64, localDeviceId);
}

/**
 * Human-readable help when assert/send fails with the standard "User N has not registered" message.
 * @param {(id: number) => string | undefined} [opts.usernameForUserId]
 */
export function formatMissingMessagingKeyHelp(error, { currentUserId, usernameForUserId } = {}) {
  const m = /User (\d+) has not registered/.exec(String(error?.message || ''));
  if (!m) return null;
  const missingId = Number(m[1]);
  const meId = currentUserId != null ? Number(currentUserId) : null;
  if (meId === missingId) {
    return (
      'This browser has not uploaded a messaging encryption key yet. Refresh the page. ' +
      'If it still fails, open DevTools → Network and check that POST .../messaging/user-keys/ succeeds (not blocked or 401).'
    );
  }
  const name = usernameForUserId ? usernameForUserId(missingId) : undefined;
  const who = name ? `${name} (user ${missingId})` : `User ${missingId}`;
  return (
    `${who} has not registered a messaging key yet. They must open Yona while logged in once ` +
    '(any page on the web, or open the mobile app) so their device can upload a public key. Then try sending again.'
  );
}

/** Send a text or image (caption) message with E2EE. */
export async function sendEncryptedUserMessage(
  messagingService,
  conversation,
  plaintext,
  messageType = 'text'
) {
  const { publicKeyB64: localPublicKeyB64 } = await ensureMessagingIdentity(minimalKeyRegistrationService);
  getLocalMessagingSecretKey(); // ensure keys were initialized (side effect: throws if missing)
  const meId = getCurrentUserIdFromToken();
  const localDeviceId = getOrCreateMessagingDeviceId();
  const rawIds = participantIdsFromConversation(conversation);
  if (rawIds.length === 0) {
    throw new Error('Conversation has no participants');
  }
  const { wrapKeys, pubMap } = await fetchWrapTargetsForSend(
    messagingService,
    rawIds,
    meId,
    localPublicKeyB64,
    localDeviceId
  );
  const e2ee_payload = buildE2eePayloadV1(plaintext ?? '', wrapKeys, pubMap);
  return messagingService.sendMessage({
    conversation_id: conversation.id,
    message_type: messageType,
    e2ee_payload,
  });
}

/**
 * Club broadcast: participant ids must match everyone in the announcement conversation after sync
 * (all club members plus creator). Pass the same set the backend will use.
 */
export async function sendClubAnnouncementEncrypted(apiClient, { clubId, participantUserIds, plaintext }) {
  const { publicKeyB64: localPublicKeyB64 } = await ensureMessagingIdentity(minimalKeyRegistrationService);
  getLocalMessagingSecretKey();
  const meId = getCurrentUserIdFromToken();
  const localDeviceId = getOrCreateMessagingDeviceId();
  const ids = [...new Set(participantUserIds)].filter((id) => id != null);
  if (ids.length === 0) {
    throw new Error('No club members to encrypt for');
  }
  const q = ids.map(String).join(',');
  const res = await apiClient.get(`/messaging/user-keys/lookup/?user_ids=${encodeURIComponent(q)}`);
  const keysRaw = res.data?.keys || {};
  const pubMap = {};
  const wrapKeys = [];
  const sortedIds = [...ids].map(Number).sort((a, b) => a - b);
  for (const uid of sortedIds) {
    const list = Array.isArray(keysRaw[String(uid)]) ? keysRaw[String(uid)] : [];
    if (list.length === 0) {
      throw new Error(
        `Member (user ${uid}) has not registered messaging keys. They must open Messages in the app once.`
      );
    }
    for (const d of list) {
      const composite = `${uid}:${d.device_id}`;
      wrapKeys.push(composite);
      if (meId != null && uid === Number(meId) && String(d.device_id) === String(localDeviceId)) {
        pubMap[composite] = localPublicKeyB64;
      } else {
        pubMap[composite] = d.public_key;
      }
    }
  }
  const e2ee_payload = buildE2eePayloadV1(plaintext ?? '', wrapKeys, pubMap);
  return apiClient.post(`/messaging/clubs/${clubId}/send-announcement/`, { e2ee_payload });
}
